import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTenant } from '../../core/contexts/TenantContext';
import { resources } from '../../core/services/resources';
import { NewCustomerDialog } from './NewCustomerDialog';
import type { Tenant } from '../../core/types/data-models';

interface TenantSwitcherProps {
  /** Stretch the trigger button to fill its container (sidebar use). */
  fullWidth?: boolean;
}

/**
 * Customer dropdown — lists every tenant in the system and lets the user
 * flip between them. Now lives at the top of the sidebar's tenant block;
 * `fullWidth` makes the trigger button match the sidebar width.
 *
 * Tenants are loaded from `/api/v1/tenants` so the switcher works even
 * before /auth/memberships is wired up.
 */
export function TenantSwitcher({ fullWidth }: TenantSwitcherProps = {}) {
  const { activeTenantId, activeTenantName, switchTenant } = useTenant();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const tenantsQuery = useQuery({
    queryKey: ['tenants', { switcher: true }],
    queryFn: () => resources.tenants.list({ pageSize: 100, sort: 'name' }),
  });

  const tenants = tenantsQuery.data?.data ?? [];

  const activeName = useMemo(() => {
    if (activeTenantName) return activeTenantName;
    const match = tenants.find((t) => t.id === activeTenantId);
    return match?.name ?? (activeTenantId ? `Tenant #${activeTenantId}` : 'Select customer');
  }, [activeTenantName, activeTenantId, tenants]);

  const open = Boolean(anchor);

  const handleSelect = (tenant: Tenant) => {
    setAnchor(null);
    if (tenant.id !== activeTenantId) {
      switchTenant(tenant.id, tenant.name);
    }
  };

  return (
    <>
      <Button
        size="small"
        fullWidth={fullWidth}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        variant="outlined"
        sx={{
          textTransform: 'none',
          minWidth: 200,
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="body2" fontWeight={600} noWrap>
          {activeName}
        </Typography>
      </Button>

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 420 } } }}
      >
        {tenantsQuery.isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">Loading customers…</Typography>
          </MenuItem>
        )}
        {!tenantsQuery.isLoading && tenants.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No customers yet</Typography>
          </MenuItem>
        )}
        {tenants.map((t) => {
          const isActive = t.id === activeTenantId;
          return (
            <MenuItem key={t.id} selected={isActive} onClick={() => handleSelect(t)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography
                  variant="body2"
                  fontWeight={isActive ? 600 : 400}
                  sx={{ flexGrow: 1 }}
                >
                  {t.name}
                </Typography>
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </MenuItem>
          );
        })}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setCreateOpen(true);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" color="primary" fontWeight={600}>
            New customer
          </Typography>
        </MenuItem>
      </Menu>

      <NewCustomerDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(tenant) => {
          // Switch into the just-created tenant so the next page load is scoped to it.
          switchTenant(tenant.id, tenant.name);
        }}
      />
    </>
  );
}
